import React, { useState, useEffect } from 'react';
import ProductRichText from '../../components/shop/ProductRichText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faSave, faTimes, faBox, faSearch } from '@fortawesome/free-solid-svg-icons';
import './AdminShopProducts.css';
import { fetchWithAuth } from '../../utils/authUtils';
import { buildProductGallery, resolveImageSrc } from '../../utils/productGallery';
import { PLACEHOLDER_50 } from '../../utils/imagePlaceholder';
import { productStockQty, stockStatusClass, stockStatusLabel } from '../../utils/productStock';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const AdminShopProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    price: '',
    stockQty: '0',
    imageUrl: '',
    imageUrls: [],
    extTitle: '',
    extContent: '',
    imageFiles: [],
  });

  // Ngăn scroll body khi modal mở
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/products`);
      const data = await response.json();
      
      if (data.success) {
        setProducts(data.data || []);
      } else {
        setError(data.message || 'Lỗi khi tải sản phẩm');
      }
    } catch (err) {
      setError('Không thể kết nối đến server');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/categories`);
      const data = await response.json();
      
      if (data.success) {
        setCategories(data.data || []);
      }
    } catch (err) {

    }
  };

  const fileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`;

  const handleImageFilesSelected = (event) => {
    const picked = Array.from(event.target.files || []);
    if (!picked.length) return;

    setFormData((prev) => {
      const existing = new Set((prev.imageFiles || []).map(fileKey));
      const merged = [...(prev.imageFiles || [])];
      picked.forEach((file) => {
        const key = fileKey(file);
        if (!existing.has(key)) {
          existing.add(key);
          merged.push(file);
        }
      });
      return { ...prev, imageFiles: merged };
    });

    event.target.value = '';
  };

  const removePendingFile = (fileToRemove) => {
    setFormData((prev) => ({
      ...prev,
      imageFiles: (prev.imageFiles || []).filter((file) => fileKey(file) !== fileKey(fileToRemove)),
    }));
  };

  const removeSavedImage = (url) => {
    setFormData((prev) => ({
      ...prev,
      imageUrls: (prev.imageUrls || []).filter((item) => item !== url),
    }));
  };

  const handleAdd = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      category: categories[0]?.name || '',
      price: '',
      stockQty: '0',
      imageUrl: '',
      imageUrls: [],
      extTitle: '',
      extContent: '',
      imageFiles: [],
    });
    setShowModal(true);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    const existingImages = buildProductGallery(product);

    setFormData({
      name: product.name,
      category: product.category,
      price: product.price,
      stockQty: String(productStockQty(product)),
      imageUrl: product.image_url || '',
      imageUrls: existingImages,
      extTitle: product.ext_title || '',
      extContent: product.ext_content || '',
      imageFiles: [],
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa sản phẩm này?')) return;

    try {
      const response = await fetchWithAuth(`${API_BASE}/api/products/${id}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      
      if (data.success) {
        alert('Xóa sản phẩm thành công');
        fetchProducts();
      } else {
        alert(data.message || 'Lỗi khi xóa sản phẩm');
      }
    } catch (err) {
      alert('Không thể kết nối đến server');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.category || !formData.price) {
      alert('Tên, danh mục và giá là bắt buộc');
      return;
    }
    const stockParsed = Number(formData.stockQty);
    if (!Number.isInteger(stockParsed) || stockParsed < 0 || stockParsed > 999999) {
      alert('Tồn kho phải là số nguyên từ 0 đến 999999');
      return;
    }

    try {
      setUploading(true);
      let imageUrls = [...(formData.imageUrls || [])];

      if (formData.imageFiles?.length) {
        const formDataUpload = new FormData();
        formData.imageFiles.forEach((file) => formDataUpload.append('images', file));

        const uploadResponse = await fetchWithAuth(`${API_BASE}/api/upload/shop/batch`, {
          method: 'POST',
          body: formDataUpload,
        });

        const uploadData = await uploadResponse.json();
        if (uploadData.success && Array.isArray(uploadData.imageUrls) && uploadData.imageUrls.length) {
          imageUrls = [...imageUrls, ...uploadData.imageUrls];
        } else {
          alert('Lỗi khi upload ảnh: ' + (uploadData.message || 'Không upload được ảnh'));
          return;
        }
      }

      imageUrls = imageUrls.filter((url, index, arr) => url && arr.indexOf(url) === index);

      if (!imageUrls.length) {
        alert('Vui lòng chọn ít nhất 1 ảnh sản phẩm');
        return;
      }

      const url = editingProduct
        ? `${API_BASE}/api/products/${editingProduct.id}`
        : `${API_BASE}/api/products`;

      const method = editingProduct ? 'PUT' : 'POST';

      const response = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          category: formData.category,
          price: parseFloat(formData.price),
          stockQty: stockParsed,
          imageUrl: imageUrls[0] || null,
          imageUrls,
          extTitle: formData.extTitle || null,
          extContent: formData.extContent || null,
          isActive: 1,
        }),
      });

      const data = await response.json();

      if (data.success) {
        alert(editingProduct ? 'Cập nhật thành công' : 'Thêm sản phẩm thành công');
        setShowModal(false);
        fetchProducts();
      } else {
        alert(data.message || 'Lỗi khi lưu sản phẩm');
      }
    } catch (err) {
      alert('Không thể kết nối đến server');
    } finally {
      setUploading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('vi-VN').format(price);
  };

  const query = keyword.trim().toLowerCase();
  const visibleProducts = query
    ? products.filter((product) => [product.name, product.category, product.id].join(' ').toLowerCase().includes(query))
    : products;

  return (
    <div className="admin-shop-products">
      <div className="page-header">
        <h2><FontAwesomeIcon icon={faBox} /> Quản lý sản phẩm</h2>
        <button className="btn-add" onClick={handleAdd}>
          <FontAwesomeIcon icon={faPlus} /> Thêm sản phẩm
        </button>
      </div>

      <div className="admin-shop-search">
        <FontAwesomeIcon icon={faSearch} />
        <input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="Tìm theo tên, danh mục hoặc mã sp"
        />
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Đang tải sản phẩm...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchProducts} className="btn-retry">Thử lại</button>
        </div>
      ) : products.length === 0 ? (
        <div className="empty-state">
          <p>Chưa có sản phẩm nào</p>
          <button className="btn-add" onClick={handleAdd}>
            <FontAwesomeIcon icon={faPlus} /> Thêm sản phẩm đầu tiên
          </button>
        </div>
      ) : (
        <div className="products-table">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Ảnh</th>
                <th>Tên sản phẩm</th>
                <th>Danh mục</th>
                <th>Giá</th>
                <th>Tồn kho</th>
                <th>Trạng thái</th>
                <th>Hành động</th>
              </tr>
            </thead>
            <tbody>
              {visibleProducts.map((product) => {
                const thumb = buildProductGallery(product)[0];
                return (
                <tr key={product.id}>
                  <td>{product.id}</td>
                  <td>
                    <img
                      src={thumb ? resolveImageSrc(thumb) : PLACEHOLDER_50}
                      alt={product.name}
                      className="product-thumb"
                      onError={(e) => { e.target.src = PLACEHOLDER_50; }}
                    />
                  </td>
                  <td>
                    <div className="product-name">{product.name}</div>
                    {product.ext_title && (
                      <div className="product-ext-title">{product.ext_title}</div>
                    )}
                  </td>
                  <td>{product.category}</td>
                  <td>₫{formatPrice(product.price)}</td>
                  <td>
                    <span className={`stock-badge ${stockStatusClass(productStockQty(product))}`}>
                      {stockStatusLabel(productStockQty(product))}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${product.is_active ? 'active' : 'inactive'}`}>
                      {product.is_active ? 'Hoạt động' : 'Ẩn'}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn-edit"
                      onClick={() => handleEdit(product)}
                      title="Sửa"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleDelete(product.id)}
                      title="Xóa"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingProduct ? 'Sửa sản phẩm' : 'Thêm sản phẩm mới'}</h3>
              <button className="btn-close" onClick={() => setShowModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group half">
                  <label>Tên sản phẩm *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Nhập tên sản phẩm"
                    required
                  />
                </div>
                <div className="form-group half">
                  <label>Danh mục *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  >
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group half">
                  <label>Giá (VNĐ) *</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="Nhập giá sản phẩm"
                    required
                  />
                </div>
                <div className="form-group half">
                  <label>Tồn kho *</label>
                  <input
                    type="number"
                    min="0"
                    max="999999"
                    step="1"
                    value={formData.stockQty}
                    onChange={(e) => setFormData({ ...formData, stockQty: e.target.value })}
                    placeholder="Số lượng có thể bán"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Hình ảnh sản phẩm (có thể chọn nhiều ảnh, chọn nhiều lần để thêm tiếp)</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageFilesSelected}
                />
                <p className="image-upload-hint">
                  Đã có {(formData.imageUrls?.length || 0) + (formData.imageFiles?.length || 0)} ảnh
                </p>
                {formData.imageUrls?.length > 0 && (
                  <div className="image-preview-grid">
                    {formData.imageUrls.map((url) => (
                      <div key={url} className="image-preview-item">
                        <img
                          src={url.startsWith('http') ? url : `${API_BASE}${url}`}
                          alt="Preview"
                        />
                        <button
                          type="button"
                          className="image-preview-remove"
                          onClick={() => removeSavedImage(url)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {formData.imageFiles?.length > 0 && (
                  <div className="image-preview-grid pending">
                    {formData.imageFiles.map((file) => (
                      <div key={fileKey(file)} className="image-preview-item">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                        />
                        <button
                          type="button"
                          className="image-preview-remove"
                          onClick={() => removePendingFile(file)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Tiêu đề phụ (tùy chọn)</label>
                <input
                  type="text"
                  value={formData.extTitle}
                  onChange={(e) => setFormData({ ...formData, extTitle: e.target.value })}
                  placeholder="Ví dụ: Hộp 30 viên"
                />
              </div>

              <div className="form-group">
                <label>Mô tả sản phẩm</label>
                <ProductRichText
                  value={formData.extContent}
                  onChange={(html) => setFormData({ ...formData, extContent: html })}
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  <FontAwesomeIcon icon={faTimes} /> Hủy
                </button>
                <button type="submit" className="btn-save" disabled={uploading}>
                  <FontAwesomeIcon icon={faSave} /> {uploading ? 'Đang lưu...' : 'Lưu'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminShopProducts;
